import { EditOutlined, VisibilityOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { CssTextField } from "src/qubCommon/Inputs";
import { getReviewCommentsCommitValue } from "./reviewCommentsDraft";

export const ReviewCommentsCell = ({
  value,
  canEdit,
  rowLabel,
  editorOrder,
  onCommit,
  onComplete,
  onFocus,
  onOpen,
}) => {
  const actionLabel = canEdit ? "Επεξεργασία" : "Προβολή";
  const isFocusedRef = useRef(false);
  const committedDraftRef = useRef(value ?? "");
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    if (isFocusedRef.current) return;

    const nextValue = value ?? "";
    committedDraftRef.current = nextValue;
    setDraft(nextValue);
  }, [value]);

  const commitDraft = () => {
    const nextValue = getReviewCommentsCommitValue({
      draft,
      value: committedDraftRef.current,
    });

    if (nextValue !== null) {
      committedDraftRef.current = nextValue;
      onCommit?.(nextValue);
    }
  };

  if (canEdit) {
    return (
      <TextField
        fullWidth
        size="small"
        variant="standard"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={(event) => {
          isFocusedRef.current = true;
          onFocus?.(event);
        }}
        onBlur={() => {
          isFocusedRef.current = false;
          commitDraft();
        }}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          event.stopPropagation();

          if (event.key === "Enter" && !event.isComposing) {
            event.preventDefault();
            commitDraft();
            onComplete?.();
          }
        }}
        InputProps={{
          disableUnderline: true,
          endAdornment: (
            <InputAdornment position="end" sx={{ mr: 0 }}>
              <Tooltip title="Επεξεργασία εκτενών σχολίων (F3)">
                <IconButton
                  aria-label={`Επεξεργασία σχολίων ελέγχου: ${rowLabel}`}
                  edge="end"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpen(draft);
                  }}
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <EditOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        inputProps={{
          "aria-label": `Σχόλια ελέγχου: ${rowLabel}`,
          "aria-keyshortcuts": "F3",
          "data-expense-review-editor": "true",
          "data-expense-review-editor-order": editorOrder,
          "data-expense-review-editor-type": "statusComments",
        }}
        sx={{
          "& .MuiInputBase-input": {
            fontSize: 13,
            px: 0.75,
            py: 0.35,
          },
        }}
      />
    );
  }

  return (
    <Tooltip title={`${actionLabel} σχολίων ελέγχου`}>
      <ButtonBase
        aria-label={`${actionLabel} σχολίων ελέγχου: ${rowLabel}`}
        onClick={(event) => {
          event.stopPropagation();
          onOpen();
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
        sx={{
          alignItems: "center",
          color: canEdit ? "text.primary" : "text.secondary",
          display: "flex",
          fontSize: 13,
          gap: 0.75,
          height: "100%",
          justifyContent: "space-between",
          px: 0.75,
          textAlign: "left",
          width: "100%",
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
          },
        }}
      >
        <Box
          component="span"
          sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
        >
          {value || (canEdit ? "  " : "-")}
        </Box>
        {canEdit ? (
          <EditOutlined aria-hidden="true" sx={{ flexShrink: 0, fontSize: 16 }} />
        ) : (
          <VisibilityOutlined aria-hidden="true" sx={{ flexShrink: 0, fontSize: 16 }} />
        )}
      </ButtonBase>
    </Tooltip>
  );
};

const ReviewCommentsDialog = ({
  selection,
  canEdit,
  onChange,
  onClose,
  onApply,
  onExited,
}) => (
  <Dialog
    open={Boolean(selection)}
    onClose={onClose}
    fullWidth
    maxWidth="md"
    disableRestoreFocus
    TransitionProps={{ onExited }}
    onKeyDown={(event) => {
      if (
        canEdit &&
        selection &&
        event.key === "Enter" &&
        (event.ctrlKey || event.metaKey) &&
        !event.isComposing
      ) {
        event.preventDefault();
        event.stopPropagation();
        onApply();
      }
    }}
  >
    <DialogTitle variant="h4">Σχόλια Ελέγχου - {selection?.rowLabel}</DialogTitle>
    <DialogContent dividers>
      {canEdit ? (
        <CssTextField
          autoFocus
          fullWidth
          multiline
          minRows={8}
          maxRows={18}
          variant="standard"
          size="small"
          fixedPadding={8}
          InputProps={{ disableUnderline: true }}
          inputProps={{ "aria-label": "Σχόλια Ελέγχου" }}
          value={selection?.draft ?? ""}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <Typography
          component="div"
          sx={{
            minHeight: 120,
            overflowWrap: "anywhere",
            whiteSpace: "pre-wrap",
          }}
        >
          {selection?.draft || "Δεν υπάρχει σχόλιο ελέγχου."}
        </Typography>
      )}
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>{canEdit ? "Άκυρο" : "Κλείσιμο"}</Button>
      {canEdit && (
        <Button
          variant="contained"
          onClick={onApply}
          aria-keyshortcuts="Control+Enter Meta+Enter"
        >
          Εφαρμογή (Ctrl+Enter)
        </Button>
      )}
    </DialogActions>
  </Dialog>
);

export default ReviewCommentsDialog;
